import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useUnsubscribeFromEmails } from '@/api/email-subscriptions/email-subscriptions.hooks';
import { InfoContainer } from './base/info-container.component';

/**
 * Public landing page for the one-click unsubscribe link in stocklist emails
 * (`/email/unsubscribe/:token`). Calls the public unsubscribe endpoint on mount
 * and reports the result — no authentication required.
 */
export function UnsubscribePage() {
  const { token } = useParams<{ token: string }>();
  const { mutate, isPending, isSuccess, isError } = useUnsubscribeFromEmails();

  // Fire exactly once per mount, even under React StrictMode double-invoke.
  const requested = useRef(false);
  useEffect(() => {
    if (requested.current || !token) return;
    requested.current = true;
    mutate(token);
  }, [token, mutate]);

  if (!token) {
    return (
      <InfoContainer
        title="Ungültiger Abmeldelink"
        description="Dieser Abmeldelink ist unvollständig. Bitte verwenden Sie den Link aus Ihrer E-Mail."
      />
    );
  }

  if (isPending) {
    return (
      <InfoContainer
        title="Abmeldung wird verarbeitet …"
        description="Einen Moment bitte."
      />
    );
  }

  if (isError) {
    return (
      <InfoContainer
        title="Abmeldung fehlgeschlagen"
        description="Der Abmeldelink ist ungültig oder abgelaufen. Bitte wenden Sie sich an eos.technik@elink-distribution.com."
        button={{ text: 'Zur Startseite', href: '/', variant: 'contained' }}
      />
    );
  }

  if (isSuccess) {
    return (
      <InfoContainer
        title="Erfolgreich abgemeldet"
        description="Sie erhalten die Bestandsliste per E-Mail nicht mehr. Sie können den Versand in Ihrem Kundenkonto jederzeit wieder aktivieren."
        button={{ text: 'Zur Startseite', href: '/', variant: 'contained' }}
      />
    );
  }

  return null;
}

export default UnsubscribePage;
